import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import type { ImageProps } from 'next/image';
import React from 'react';

import { type BackgroundColorProps, Box } from '@/components/Box';
import { Heading } from '@/components/Cms/Heading';
import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { ContentfulPlaceholderServiceFragment } from '@/graphql/contentful/api';
import type { WithChildren, WithHTMLProp } from '@/lib/types';
import { theme } from '@/theme';

import * as S from './FormSearch.styles';

export type FormSearchProps = ContentfulPlaceholderServiceFragment &
  WithHTMLProp<'className'> &
  WithChildren &
  BackgroundColorProps & {
    hiddenTitle?: boolean;
    icon?: ImageProps['src'];
  };

export const FormSearch = ({
  backgroundColor,
  bg,
  children,
  className,
  hiddenTitle,
  icon,
  placeholderHeading,
  subtext,
}: FormSearchProps) => {
  return (
    <Container
      backgroundColor={backgroundColor}
      bg={bg}
      className={className}
      data-test-id='FormSearchContainer'
      disableGutters
      maxWidth='xl'
      py={5}
    >
      <S.Content
        className='content'
        data-test-id='FormSearchContent'
        maxWidth='sm'
      >
        {icon && <S.Image alt='' height={80} src={icon} width={80} />}
        {!hiddenTitle && placeholderHeading && (
          <Heading
            Heading={placeholderHeading}
            color={placeholderHeading?.mainColor ?? theme.colors.default}
            data-test-id='FormSearchHeading'
          />
        )}
        {subtext && (
          <Typography
            as={Box}
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(subtext.json),
            }}
            data-test-id='FormSearchSubtext'
            textAlign='center'
          ></Typography>
        )}
        <Flex alignItems='center' flexDirection='column' mt={3}>
          {children}
        </Flex>
      </S.Content>
    </Container>
  );
};

FormSearch.displayName = 'FormSearch';
